import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { getEmailStatus } from '../../../../helpers/email.helpers';
import { useLoader } from '../../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../../models/error.model';
import { useAuthService } from '../../../auth/auth.service';
import { NotificationLevel, ToastNotification } from '../../../notifications/models/notification.model';
import { INotificationsService } from '../../../notifications/services/notifications.service';
import { PersonalInformationWidget } from '../../components/personalInformation/PersonalInformationWidget';
import { IUserService } from '../../user.service';

export const PersonalInformationWidgetContainer = observer((): ReactElement => {
  const userService = useInjection(IUserService.$);
  const notificationService = useInjection(INotificationsService.$);
  const authService = useAuthService();

  const { withLoaderAsync } = useLoader();

  const [fullName, setFullName] = useState(userService.data.userInfo.fullName);
  const [company, setCompany] = useState(userService.data.userInfo.company);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [oldPasswordStatus, setOldPasswordStatus] = useState(InputStatus.empty());
  const [newPasswordStatus, setNewPasswordStatus] = useState(InputStatus.empty());
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(InputStatus.empty());
  const [password, setPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState(InputStatus.empty());

  useEffect(() => {
    void userService.getUserInfoAsync();
  }, []);

  const handleFullNameChange = (value: string): void => {
    setFullName(value);
  };

  const handleCompanyChange = (value: string): void => {
    setCompany(value);
  };

  const handleFullNameBlur = async (value: string): Promise<void> => {
    if (userService.data.userInfo.fullName !== value) {
      await userService.updateUserDetails(fullName, company, userService.data.userInfo.country);
    }
  };

  const handleCompanyBlur = async (value: string): Promise<void> => {
    if (userService.data.userInfo.company !== value) {
      await userService.updateUserDetails(fullName, company, userService.data.userInfo.country);
    }
  };

  const handleShowChangePassword = (): void => {
    setShowPasswordModal(true);
    resetPasswordState();
  };

  const handleHideChangePassword = (): void => {
    setShowPasswordModal(false);
    resetPasswordState();
  };

  const resetPasswordState = (): void => {
    setNewPassword('');
    setOldPassword('');
    setOldPasswordStatus(InputStatus.empty());
    setNewPasswordStatus(InputStatus.empty());
  };

  const handleNewPasswordChange = (newPassword: string): void => {
    setNewPassword(newPassword);
  };

  const handleOldPasswordChange = (oldPassword: string): void => {
    setOldPassword(oldPassword);
  };

  const handleNewPasswordStatusChange = (newPasswordState: InputStatus): void => {
    setNewPasswordStatus(newPasswordState);
  };

  const validateOldPassword = (): boolean => {
    if (oldPassword === '') {
      setOldPasswordStatus(new InputStatus(false, ['field_cant_be_empty']));
      return false;
    }
    setOldPasswordStatus(InputStatus.valid());
    return true;
  };

  const handleChangePassword = async (): Promise<void> => {
    const isNewPasswordValid = newPasswordStatus.isValid;
    const isOldPasswordValid = validateOldPassword();
    if (!isNewPasswordValid || !isOldPasswordValid) {
      return;
    }
    const result = await withLoaderAsync(async () => await userService.changeUserPasswordAsync(oldPassword, newPassword), 'change_password_loader');
    if (result instanceof ACDLError) {
      if (result.apiErrorResponse?.errorCodes?.includes('password_mismatch')) {
        setOldPasswordStatus(new InputStatus(false, ['password_mismatch']));
      } else {
        await notificationService.push(new ToastNotification(NotificationLevel.ERROR, 'change_user_password_failed'));
      }
      return;
    }
    await notificationService.push(new ToastNotification(NotificationLevel.SUCCESS, 'password_changed_successfully'));
    handleHideChangePassword();
  };

  const handleShowDeleteAccountModal = (): void => {
    setShowDeleteAccountModal(true);
  };

  const handleHideDeleteAccountModal = (): void => {
    setShowDeleteAccountModal(false);
  };

  const handleDeleteAccount = async (): Promise<void> => {
    const result = await withLoaderAsync(async () => userService.deleteUserAsync(), 'confirm-delete');
    if (result instanceof ACDLError) {
      return;
    }
    handleHideDeleteAccountModal();
    authService.logout();
  };

  const handleShowChangeEmailModal = (): void => {
    setShowChangeEmailModal(true);
    resetChangeEmailState();
  };

  const handleHideChangeEmailModal = (): void => {
    setShowChangeEmailModal(false);
    resetChangeEmailState();
  };

  const resetChangeEmailState = (): void => {
    setEmail('');
    setPassword('');
    setEmailStatus(InputStatus.empty());
    setPasswordStatus(InputStatus.empty());
  };

  const handlePasswordChange = (password: string): void => {
    setPassword(password);
    setPasswordStatus(password ? InputStatus.valid() : new InputStatus(false, ['field_cant_be_empty']));
  };

  const handlePasswordBlur = (): void => {
    setPasswordStatus(password ? InputStatus.valid() : new InputStatus(false, ['field_cant_be_empty']));
  };

  const handleEmailChange = (email: string): void => {
    setEmail(email);
    setEmailStatus(getEmailStatus(email || '', [], true));
  };

  const handleEmailBlur = (): void => {
    setEmailStatus(getEmailStatus(email || '', [], true));
  };

  const handleChangeEmailConfirmation = async (): Promise<void> => {
    if (password && email) {
      const result = await withLoaderAsync(async () => userService.changeEmail(password, email), 'change_email_loader');
      if (result instanceof ACDLError) {
        if (result.isBadRequestWithCode(['EMPTY_EMAIL'])) {
          setEmailStatus(new InputStatus(false, ['field_cant_be_empty']));
        } else if (result.isBadRequestWithCode(['PASSWORD_MISMATCH'])) {
          setPasswordStatus(new InputStatus(false, ['password_mismatch']));
        } else if (result.isBadRequestWithCode(['EMAIL_TAKEN'])) {
          setEmailStatus(new InputStatus(false, ['email_taken']));
        }
      } else {
        setShowChangeEmailModal(false);
      }
    }
  };

  return (
    <PersonalInformationWidget
      email={userService.data.userInfo.email}
      emailChangePending={userService.data.userInfo.emailChangePending}
      fullName={fullName}
      company={company}
      onFullNameChange={handleFullNameChange}
      onCompanyChange={handleCompanyChange}
      onFullNameBlur={handleFullNameBlur}
      onCompanyBlur={handleCompanyBlur}
      onShowChangePasswordModal={handleShowChangePassword}
      onHideChangePasswordModal={handleHideChangePassword}
      onNewPasswordChange={handleNewPasswordChange}
      onOldPasswordChange={handleOldPasswordChange}
      onOldPasswordBlur={validateOldPassword}
      onNewPasswordStatusChange={handleNewPasswordStatusChange}
      showPasswordModal={showPasswordModal}
      oldPasswordStatus={oldPasswordStatus}
      passwordFormIsValid={oldPasswordStatus.isValid !== false && newPasswordStatus.isValid !== false}
      onResetPassword={handleChangePassword}
      showDeleteAccountModal={showDeleteAccountModal}
      onShowDeleteAccountModal={handleShowDeleteAccountModal}
      onHideDeleteAccountModal={handleHideDeleteAccountModal}
      onDeleteAccount={handleDeleteAccount}
      onShowChangeEmailModal={handleShowChangeEmailModal}
      onHideChangeEmailModal={handleHideChangeEmailModal}
      showChangeEmailModal={showChangeEmailModal}
      emailStatus={emailStatus}
      onChangeEmailConfirmation={handleChangeEmailConfirmation}
      onEmailBlur={handleEmailBlur}
      onEmailChange={handleEmailChange}
      onPasswordBlur={handlePasswordBlur}
      onPasswordChange={handlePasswordChange}
      passwordStatus={passwordStatus}
    />
  );
});
